// A6 — Auditoría del tenant (Admin Tenant). Eventos de todo el tenant, solo
// lectura, filtros + paginación + detalle. Mismo contrato que C14/D5.

import { useState } from 'react';
import type { AuditoriaQuery, EventoAuditoria } from 'schemas';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { TBody, TD, TH, THead, TR, Table } from '../../components/ui/table';
import { RoleLayout } from '../../components/layout/RoleLayout';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { useAdminAuditoria } from '../../lib/api/queries';
import { cn } from '../../lib/utils';

const TIPOS_EVENTO = [
  'create',
  'update',
  'delete',
  'soft_delete',
  'restore',
  'login',
  'logout',
  'suplantacion_inicio',
  'suplantacion_fin',
  'export',
  'import',
] as const;
type TipoEvento = (typeof TIPOS_EVENTO)[number];

export function A6AuditoriaPage() {
  const [filtros, setFiltros] = useState<AuditoriaQuery>({ limit: 100, offset: 0 });
  const [detalle, setDetalle] = useState<EventoAuditoria | null>(null);
  const { data, isLoading, isError } = useAdminAuditoria(filtros);

  if (isLoading || !data) {
    return (
      <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner />
        </div>
      </RoleLayout>
    );
  }
  if (isError) {
    return (
      <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
        <div className="flex min-h-[60vh] items-center justify-center text-sm text-crit">
          No se pudo cargar la auditoría.
        </div>
      </RoleLayout>
    );
  }

  const pageActual = Math.floor(filtros.offset / filtros.limit) + 1;
  const totalPaginas = Math.max(1, Math.ceil(data.total / filtros.limit));

  return (
    <RoleLayout sidebar={<AdminSidebar />} roleLabel="Admin del Tenant">
      <div className="flex flex-col gap-4">
        <header>
          <h1 className="text-xl font-medium text-fg">Auditoría del tenant</h1>
          <p className="mt-1 text-[11px] text-fg-tertiary">
            {data.tenant.nombre} · {data.total} evento(s) · solo lectura
          </p>
        </header>

        <Card>
          <CardContent className="grid grid-cols-1 gap-3 p-4 pt-4 md:grid-cols-3 xl:grid-cols-6">
            <div className="flex flex-col gap-1">
              <Label htmlFor="a6-desde">Desde</Label>
              <Input
                id="a6-desde"
                type="date"
                value={filtros.desde ?? ''}
                onChange={(e) =>
                  setFiltros({ ...filtros, desde: e.target.value || undefined, offset: 0 })
                }
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="a6-hasta">Hasta</Label>
              <Input
                id="a6-hasta"
                type="date"
                value={filtros.hasta ?? ''}
                onChange={(e) =>
                  setFiltros({ ...filtros, hasta: e.target.value || undefined, offset: 0 })
                }
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="a6-actor">Actor</Label>
              <Select
                id="a6-actor"
                value={filtros.user_id ?? ''}
                onChange={(e) =>
                  setFiltros({ ...filtros, user_id: e.target.value || undefined, offset: 0 })
                }
              >
                <option value="">Todos</option>
                {data.recursos.actores.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.nombre} {a.apellido}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="a6-tipo">Tipo evento</Label>
              <Select
                id="a6-tipo"
                value={filtros.tipo_evento ?? ''}
                onChange={(e) =>
                  setFiltros({
                    ...filtros,
                    tipo_evento: (e.target.value || undefined) as TipoEvento | undefined,
                    offset: 0,
                  })
                }
              >
                <option value="">Todos</option>
                {TIPOS_EVENTO.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="a6-entidad">Entidad</Label>
              <Select
                id="a6-entidad"
                value={filtros.entidad_tipo ?? ''}
                onChange={(e) =>
                  setFiltros({ ...filtros, entidad_tipo: e.target.value || undefined, offset: 0 })
                }
              >
                <option value="">Todas</option>
                {data.recursos.entidades_tipo.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor="a6-q">Buscar razón</Label>
              <Input
                id="a6-q"
                value={filtros.q ?? ''}
                onChange={(e) =>
                  setFiltros({ ...filtros, q: e.target.value || undefined, offset: 0 })
                }
              />
            </div>
          </CardContent>
        </Card>

        {data.eventos.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-fg-tertiary">
              Sin eventos con esos filtros.
            </CardContent>
          </Card>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>Fecha</TH>
                <TH>Actor</TH>
                <TH align="center">Tipo</TH>
                <TH>Entidad</TH>
                <TH>Campo</TH>
                <TH>Razón</TH>
                <TH align="right" />
              </TR>
            </THead>
            <TBody>
              {data.eventos.map((e) => (
                <TR key={e.id}>
                  <TD className="font-mono text-xs text-fg-secondary">
                    {new Date(e.created_at).toLocaleString('es-CO', {
                      year: '2-digit',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TD>
                  <TD className="text-xs">
                    {e.actor_nombre || e.actor_apellido ? (
                      <>
                        <span className="font-medium text-fg">
                          {e.actor_nombre} {e.actor_apellido}
                        </span>
                        <div className="text-fg-tertiary">{e.actor_email}</div>
                      </>
                    ) : (
                      <span className="text-fg-tertiary">{e.actor_email ?? '—'}</span>
                    )}
                    {e.ejecutada_via_suplantacion && (
                      <span className="ml-1 inline-block rounded bg-crit/10 px-1 py-0.5 text-[9px] uppercase text-crit">
                        suplant
                      </span>
                    )}
                  </TD>
                  <TD align="center">
                    <span
                      className={cn(
                        'inline-block rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider',
                        e.tipo_evento === 'create'
                          ? 'bg-ok/10 text-ok'
                          : e.tipo_evento === 'soft_delete' || e.tipo_evento === 'delete'
                            ? 'bg-crit/10 text-crit'
                            : e.tipo_evento === 'restore'
                              ? 'bg-info/10 text-info'
                              : 'bg-warn/10 text-warn',
                      )}
                    >
                      {e.tipo_evento}
                    </span>
                  </TD>
                  <TD className="text-xs text-fg-secondary">{e.entidad_tipo}</TD>
                  <TD className="text-xs text-fg-secondary">{e.campo_cambiado ?? '—'}</TD>
                  <TD className="max-w-md truncate text-xs text-fg">{e.razon ?? '—'}</TD>
                  <TD align="right">
                    <Button size="sm" variant="ghost" onClick={() => setDetalle(e)}>
                      Ver
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}

        {totalPaginas > 1 && (
          <div className="flex items-center justify-between border-t border-border pt-3">
            <span className="text-xs text-fg-tertiary">
              Página {pageActual} de {totalPaginas} · {data.total} eventos
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={filtros.offset === 0}
                onClick={() =>
                  setFiltros({ ...filtros, offset: Math.max(0, filtros.offset - filtros.limit) })
                }
              >
                ← Anterior
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={pageActual >= totalPaginas}
                onClick={() => setFiltros({ ...filtros, offset: filtros.offset + filtros.limit })}
              >
                Siguiente →
              </Button>
            </div>
          </div>
        )}
      </div>

      {detalle && <DetalleEventoDialog evento={detalle} onClose={() => setDetalle(null)} />}
    </RoleLayout>
  );
}

function DetalleEventoDialog({
  evento,
  onClose,
}: {
  evento: EventoAuditoria;
  onClose: () => void;
}) {
  return (
    <Dialog open onClose={onClose} className="max-w-2xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Detalle del evento</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3 text-sm">
          <Row label="ID" value={evento.id} mono />
          <Row label="Fecha" value={new Date(evento.created_at).toLocaleString('es-CO')} />
          <Row
            label="Actor"
            value={
              evento.actor_nombre
                ? `${evento.actor_nombre} ${evento.actor_apellido ?? ''} (${evento.actor_email})`
                : (evento.actor_email ?? evento.user_id_actor)
            }
          />
          {evento.ejecutada_via_suplantacion && <Row label="Suplantación" value="Sí" />}
          <Row label="Tipo evento" value={evento.tipo_evento} />
          <Row
            label="Entidad"
            value={`${evento.entidad_tipo}${evento.entidad_id ? ` (${evento.entidad_id})` : ''}`}
            mono
          />
          {evento.campo_cambiado && <Row label="Campo cambiado" value={evento.campo_cambiado} />}
          {evento.razon && <Row label="Razón" value={evento.razon} />}
          {evento.valor_anterior !== null && (
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Valor anterior
              </span>
              <pre className="max-h-32 overflow-auto rounded border border-border bg-surface-elevated p-2 font-mono text-[11px] text-fg">
                {JSON.stringify(evento.valor_anterior, null, 2)}
              </pre>
            </div>
          )}
          {evento.valor_nuevo !== null && (
            <div className="flex flex-col gap-1">
              <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
                Valor nuevo
              </span>
              <pre className="max-h-32 overflow-auto rounded border border-border bg-surface-elevated p-2 font-mono text-[11px] text-fg">
                {JSON.stringify(evento.valor_nuevo, null, 2)}
              </pre>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-fg-tertiary">
        {label}
      </span>
      <span className={cn('text-fg', mono && 'font-mono text-xs')}>{value}</span>
    </div>
  );
}
